import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
} from '@mui/material'
import { EventNote, Build } from '@mui/icons-material'
import { usePlantContext } from '../hooks/usePlantContext'
import { api } from '../services/api'
import LoadingState from '../components/common/LoadingState'
import CapacityCell from '../components/capacity/CapacityCell'
import MaintenanceDialog from '../components/capacity/MaintenanceDialog'

const MONTHS = [
  { key: 'apr26', label: 'Apr' },
  { key: 'may26', label: 'May' },
  { key: 'jun26', label: 'Jun' },
  { key: 'jul26', label: 'Jul' },
  { key: 'aug26', label: 'Aug' },
  { key: 'sep26', label: 'Sep' },
  { key: 'oct26', label: 'Oct' },
  { key: 'nov26', label: 'Nov' },
  { key: 'dec26', label: 'Dec' },
  { key: 'jan27', label: 'Jan' },
  { key: 'feb27', label: 'Feb' },
  { key: 'mar27', label: 'Mar' },
]

const LINES = ['Line 1', 'Line 2', 'Line 3', 'Line 4']

// -- Fallback demo data used when API returns empty/null --
function generateDemoData() {
  const baseAvailable = {
    'Line 1': [168, 160, 176, 168, 160, 176, 168, 152, 176, 168, 160, 176],
    'Line 2': [168, 160, 176, 168, 160, 176, 168, 152, 176, 168, 160, 176],
    'Line 3': [168, 160, 176, 168, 160, 176, 168, 152, 176, 168, 160, 176],
    'Line 4': [168, 160, 176, 168, 160, 176, 168, 152, 176, 168, 160, 176],
  }

  const maintenance = {
    'Line 1': [0, 0, 0, 24, 0, 0, 16, 0, 0, 0, 0, 0],
    'Line 2': [0, 8, 0, 0, 0, 16, 0, 0, 0, 24, 0, 0],
    'Line 3': [0, 0, 16, 0, 0, 0, 0, 8, 0, 0, 0, 24],
    'Line 4': [8, 0, 0, 0, 16, 0, 0, 0, 0, 0, 8, 0],
  }

  const scheduled = {
    'Line 1': [172, 138, 152, 126, 148, 155, 130, 140, 165, 145, 135, 158],
    'Line 2': [145, 139, 168, 150, 142, 148, 155, 130, 162, 125, 150, 160],
    'Line 3': [140, 128, 145, 158, 130, 162, 148, 120, 155, 142, 135, 130],
    'Line 4': [150, 148, 155, 142, 130, 165, 145, 135, 170, 155, 140, 168],
  }

  const data = {}
  LINES.forEach((line) => {
    data[line] = MONTHS.map((_, i) => {
      const netAvail = baseAvailable[line][i] - maintenance[line][i]
      const sched = scheduled[line][i]
      const util = (sched / netAvail) * 100
      return {
        scheduledHours: sched,
        netAvailableHours: netAvail,
        utilizationPercent: Math.round(util * 10) / 10,
      }
    })
  })
  return data
}

function groupCellsByLine(cells) {
  const grouped = {}
  cells.forEach((cell) => {
    const lineName = cell.line
    if (!grouped[lineName]) grouped[lineName] = []
    grouped[lineName].push({
      scheduledHours: cell.scheduledHours,
      netAvailableHours: cell.netAvailableHours,
      utilizationPercent: cell.utilizationPercent,
    })
  })
  return grouped
}

export default function CapacityCalendar() {
  const navigate = useNavigate()
  const { plant } = usePlantContext()
  const [maintenanceOpen, setMaintenanceOpen] = useState(false)
  const [maintenanceEvents, setMaintenanceEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [capacityData, setCapacityData] = useState(() => generateDemoData())

  useEffect(() => {
    let cancelled = false
    async function fetchCapacity() {
      setLoading(true)
      setError(null)
      try {
        const data = await api.get(`/capacity?plant=${plant.plantCode}`)
        if (cancelled) return
        if (data?.length) {
          setCapacityData(groupCellsByLine(data))
        }
        // fallback: keep demo data if API returns empty/null
      } catch (err) {
        if (cancelled) return
        setError(err.message)
        // fallback: keep demo data on error
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchCapacity()
    return () => { cancelled = true }
  }, [plant.plantCode])

  const lines = useMemo(() => {
    return Object.keys(capacityData).length ? Object.keys(capacityData) : LINES
  }, [capacityData])

  const totals = useMemo(() => {
    return MONTHS.map((_, mi) => {
      let totalScheduled = 0
      let totalAvailable = 0
      lines.forEach((line) => {
        if (capacityData[line]?.[mi]) {
          totalScheduled += capacityData[line][mi].scheduledHours
          totalAvailable += capacityData[line][mi].netAvailableHours
        }
      })
      return {
        scheduledHours: totalScheduled,
        netAvailableHours: totalAvailable,
        utilizationPercent: totalAvailable > 0
          ? Math.round((totalScheduled / totalAvailable) * 1000) / 10
          : 0,
      }
    })
  }, [capacityData, lines])

  const overloadCount = useMemo(() => {
    let count = 0
    lines.forEach((line) => {
      capacityData[line]?.forEach((cell) => {
        if (cell.utilizationPercent > 100) count++
      })
    })
    return count
  }, [capacityData, lines])

  const cautionCount = useMemo(() => {
    let count = 0
    lines.forEach((line) => {
      capacityData[line]?.forEach((cell) => {
        if (cell.utilizationPercent >= 85 && cell.utilizationPercent <= 100) count++
      })
    })
    return count
  }, [capacityData, lines])

  const handleCellClick = (line, monthIndex) => {
    const lineNum = line.replace('Line ', '')
    // NOTE: the planning board is mounted at /plans/:planId/grid, not /board.
    // Without a plan id in scope here we send the user to the plan list rather
    // than to a route that does not exist (FAR-6).
    navigate(`/plans?line=${lineNum}&month=${MONTHS[monthIndex].key}`)
  }

  const handleMaintenanceSave = async (data) => {
    try {
      await api.post('/capacity/maintenance', {
        plant: plant.plantCode,
        line: data.line,
        month: data.month,
        hours: data.hours,
        description: data.description,
      })
      setMaintenanceEvents((prev) => [...prev, data])
    } catch (err) {
      setError(err.message)
      setMaintenanceEvents((prev) => [...prev, data])
    }
  }

  if (loading && !Object.keys(capacityData).length) {
    return <LoadingState message="Loading capacity data…" />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <EventNote color="primary" sx={{ fontSize: 22 }} />
        <Typography variant="h3" sx={{ flex: 1 }}>Capacity Calendar</Typography>
        <Button
          variant="outlined"
          startIcon={<Build />}
          size="small"
          onClick={() => setMaintenanceOpen(true)}
        >
          Add Maintenance
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {plant.name} — FY 2026-27
        </Typography>
        {overloadCount > 0 && (
          <Chip
            label={`${overloadCount} overloaded`}
            size="small"
            sx={{ backgroundColor: 'error.light', color: 'error.dark', fontWeight: 600 }}
          />
        )}
        {cautionCount > 0 && (
          <Chip
            label={`${cautionCount} caution`}
            size="small"
            sx={{ backgroundColor: 'warning.light', color: 'warning.dark', fontWeight: 600 }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'surface.containerLow',
                  zIndex: 2,
                  minWidth: 100,
                }}
              >
                Line
              </TableCell>
              {MONTHS.map((m) => (
                <TableCell
                  key={m.key}
                  align="center"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', minWidth: 110 }}
                >
                  {m.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line} hover>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    borderRight: '2px solid',
                    borderRightColor: 'divider',
                  }}
                >
                  {line}
                </TableCell>
                {(capacityData[line] || []).map((cell, mi) => (
                  <TableCell key={MONTHS[mi]?.key} sx={{ p: 0.5 }}>
                    <CapacityCell
                      scheduledHours={cell.scheduledHours}
                      netAvailableHours={cell.netAvailableHours}
                      utilizationPercent={cell.utilizationPercent}
                      onClick={() => handleCellClick(line, mi)}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}

            <TableRow sx={{ '& td': { borderTop: '2px solid', borderTopColor: 'divider' } }}>
              <TableCell
                sx={{
                  fontWeight: 700,
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'surface.containerLow',
                  zIndex: 1,
                  borderRight: '2px solid',
                  borderRightColor: 'divider',
                }}
              >
                Totals
              </TableCell>
              {totals.map((cell, mi) => (
                <TableCell key={MONTHS[mi].key} sx={{ p: 0.5 }}>
                  <CapacityCell
                    scheduledHours={cell.scheduledHours}
                    netAvailableHours={cell.netAvailableHours}
                    utilizationPercent={cell.utilizationPercent}
                    isTotal
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {maintenanceEvents.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Scheduled Maintenance ({maintenanceEvents.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {maintenanceEvents.map((evt, i) => (
              <Chip
                key={i}
                label={`${evt.line} · ${evt.month} · ${evt.hours}h — ${evt.description || 'No description'}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      <MaintenanceDialog
        open={maintenanceOpen}
        onClose={() => setMaintenanceOpen(false)}
        onSave={handleMaintenanceSave}
      />
    </Box>
  )
}
